import React, { useEffect, useState } from 'react';
import {
  Card, Table, Button, Space, message, Switch, Popconfirm, Tag, Typography, Image,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  ArrowUpOutlined, ArrowDownOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { apiClient } from 'Utils/api';
import { useLanguage } from '../../Context/LanguageContext';

const { Title } = Typography;

const apiBase = process.env.REACT_APP_API_URL || '';
const resolveImg = (url: string) => /^https?:\/\//i.test(url) ? url : `${apiBase}${url}`;

interface HeroSlide {
  id: string;
  imageUrl: string;
  title: Record<string, string> | null;
  subtitle: Record<string, string> | null;
  ctaUrl: string | null;
  sortOrder: number;
  isActive: boolean;
}

const HeroSliderPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const [slides, setSlides] = useState<HeroSlide[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/admin/hero-slides');
      setSlides(res.data.heroSlides || []);
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler beim Laden');
    } finally { setLoading(false); }
  };

  const remove = async (id: string) => {
    try {
      await apiClient.delete(`/api/admin/hero-slides/${id}`);
      message.success('Slide gelöscht');
      fetchAll();
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler');
    }
  };

  const toggleActive = async (slide: HeroSlide, isActive: boolean) => {
    try {
      await apiClient.patch(`/api/admin/hero-slides/${slide.id}`, { isActive });
      fetchAll();
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler');
    }
  };

  const move = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= slides.length) return;
    const a = slides[index];
    const b = slides[target];
    try {
      await Promise.all([
        apiClient.patch(`/api/admin/hero-slides/${a.id}`, { sortOrder: b.sortOrder }),
        apiClient.patch(`/api/admin/hero-slides/${b.id}`, { sortOrder: a.sortOrder }),
      ]);
      fetchAll();
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler');
    }
  };

  const columns = [
    {
      title: 'Reihenfolge',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 140,
      render: (so: number, _r: HeroSlide, i: number) => (
        <Space>
          <span style={{ minWidth: 24, display: 'inline-block' }}>{so}</span>
          <Button size="small" icon={<ArrowUpOutlined />} disabled={i === 0} onClick={() => move(i, -1)} />
          <Button size="small" icon={<ArrowDownOutlined />} disabled={i === slides.length - 1} onClick={() => move(i, 1)} />
        </Space>
      ),
    },
    {
      title: 'Bild',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 140,
      render: (url: string) => (
        <Image
          src={resolveImg(url)}
          alt=""
          width={120}
          height={70}
          style={{ objectFit: 'cover', borderRadius: 4 }}
          fallback="/placeholder.svg"
        />
      ),
    },
    {
      title: 'Titel',
      key: 'title',
      render: (_: any, r: HeroSlide) => {
        const titleCurrent = r.title?.[currentLanguage];
        const titleFallback = r.title?.de;
        const subCurrent = r.subtitle?.[currentLanguage];
        const subFallback = r.subtitle?.de;
        const titleShown = titleCurrent || titleFallback || '–';
        const subShown = subCurrent || subFallback || '';
        const isFallback = !titleCurrent && !!titleFallback && currentLanguage !== 'de';
        return (
          <div>
            <div style={{ fontWeight: 600 }}>
              {titleShown}
              {isFallback && (
                <Tag color="gold" style={{ marginLeft: 8 }}>DE-Fallback</Tag>
              )}
            </div>
            {subShown && <div style={{ color: '#888', fontSize: 12 }}>{subShown}</div>}
          </div>
        );
      },
    },
    {
      title: 'CTA-Link',
      dataIndex: 'ctaUrl',
      key: 'ctaUrl',
      width: 140,
      render: (v: string | null) => v ? <Tag>{v}</Tag> : '–',
    },
    {
      title: 'Aktiv',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      render: (v: boolean, r: HeroSlide) => (
        <Switch checked={v} onChange={(c) => toggleActive(r, c)} />
      ),
    },
    {
      title: 'Aktionen',
      key: 'actions',
      width: 180,
      render: (_: any, r: HeroSlide) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => navigate(`/hero-slider/${r.id}`)}
            data-testid={`edit-slide-${r.id}`}
          >
            Bearbeiten
          </Button>
          <Popconfirm title="Slide löschen?" onConfirm={() => remove(r.id)} okText="Löschen" cancelText="Abbrechen">
            <Button danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={3}>Home-Slider</Title>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/hero-slider/new')}
            data-testid="add-slide-btn"
          >
            Slide hinzufügen
          </Button>
        </div>
        <Table rowKey="id" dataSource={slides} columns={columns} loading={loading} pagination={false} />
      </Card>
    </div>
  );
};

export default HeroSliderPage;
